import React, { useState } from "react";
import styled from "styled-components";
import { DEFAULT_PROJECT_PALETTE, PROJECT_PALETTES } from "@lifeat/shared/projects/ProjectsConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CaretIconToggle from "@common/Icons/CaretIconToggle";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { DropdownItem } from "../../task-management/components/TaskTagDropdownComponents";
import { UpdateProjectColorEvent } from "../AmplitudeProjectsEvents";
import { ProjectsService } from "../ProjectsService";

const ProjectColorWrapper = styled(DropdownMenuComponents.Trigger)`
    border-radius: ${Styles.borderRadiusSharp};
    border: 1px solid ${(props) => props.theme.borderPrimary};
    padding: 4px 8px;
`;

const ProjectColorTrigger = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    width: fit-content;
    max-width: 150px;
    height: fit-content;

    padding: 2px;
    font-size: ${FontSize.normal};
    border-radius: 2px;

    cursor: pointer;
    overflow: hidden;
    color: ${(props) => props.theme.textColorPrimary};
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const ColorPreview = styled.div<{ colorHex: string }>`
    height: 20px;
    width: 20px;
    border-radius: 5px;
    background-color: ${(props) => props.colorHex};
`;

const RadioButtonItemContainer = styled.div`
    display: flex;
    gap: 7px;
`;

const ColorSelectorLabelContainer = styled.label`
    width: 100%;
    display: flex;
    gap: 10px;
`;

interface ProjectColorPickerProps {
    readonly projectId: string;
    readonly color: string;
}
export default function ProjectColorPicker(props: ProjectColorPickerProps) {
    const [currProjectColor, setCurrProjectColor] = useProjectColorInput(props.projectId, props.color);
    const [isColorPopoverOpen, setIsColorPopoverOpen] = useState(false);

    return (
        <DropdownMenuComponents.Root
            open={isColorPopoverOpen}
            onOpenChange={(open: boolean) => setIsColorPopoverOpen(open)}
        >
            <ProjectColorWrapper>
                <ProjectColorTrigger>
                    <ColorPreview colorHex={currProjectColor} />
                    {PROJECT_PALETTES.find((color) => color.primary === currProjectColor).label}
                    <CaretIconToggle showOpen={isColorPopoverOpen} />
                </ProjectColorTrigger>
            </ProjectColorWrapper>

            <DropdownMenuComponents.Content>
                <DropdownMenuComponents.HeaderItem label={"Project Color"} />

                {PROJECT_PALETTES.map((colorPalette) => (
                    <DropdownItem
                        key={`project-color-picker-option-${colorPalette.label}`}
                        onClick={() => setCurrProjectColor(colorPalette.primary)}
                    >
                        <RadioButtonItemContainer>
                            <input
                                id={`project-color-option-${colorPalette.label}`}
                                type="radio"
                                value={colorPalette.primary}
                                checked={colorPalette.primary === currProjectColor}
                            />
                            <ColorSelectorLabelContainer htmlFor={`project-color-option-${colorPalette.label}`}>
                                <ColorPreview colorHex={colorPalette.primary} />
                                <ProjectColorTrigger>{colorPalette.label}</ProjectColorTrigger>
                            </ColorSelectorLabelContainer>
                        </RadioButtonItemContainer>
                    </DropdownItem>
                ))}
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}

function useProjectColorInput(
    projectId: string,
    initialColor: string,
): [currProjectColor: string, setCurrProjectColor: (newColor: string) => void] {
    const amplitudeService = AmplitudeService.getInstance();

    const [currProjectColor, setCurrProjectColor] = useDebouncedInput(
        initialColor ?? DEFAULT_PROJECT_PALETTE.primary,
        async (updatedColor: string) => {
            if (updatedColor !== initialColor) {
                amplitudeService.logEvent(new UpdateProjectColorEvent());
                await ProjectsService.updateProjectColor(projectId, updatedColor);
            }
        },
        1000,
    );

    return [currProjectColor, setCurrProjectColor];
}
